/**
 * 업로드 큐 실행기.
 *
 * 문서 여러 건을 올릴 때 순차로 돌리면 10개만 넘어도 체감이 크게 나빠지고,
 * 반대로 전부 동시에 던지면 브라우저 커넥션 한도(호스트당 6)에 걸려 뒤쪽
 * 요청이 진행률조차 못 받는다. 그래서 동시 실행 수를 제한한다.
 *
 * React 밖의 순수 함수로 둔 이유는 동시성·재시도 규칙이 이 기능에서 유일하게
 * 틀리기 쉬운 부분이라 DOM 없이 검증하고 싶기 때문이다.
 */

/** 브라우저 커넥션 한도(호스트당 6)를 넘지 않으면서 체감이 충분한 값. */
export const DEFAULT_UPLOAD_CONCURRENCY = 3;

/**
 * items 를 최대 limit 개씩 동시에 worker 로 흘린다.
 *
 * worker 가 throw 해도 큐는 멈추지 않는다 — 한 파일이 실패했다고 나머지를
 * 안 올리면 사용자가 전부 다시 골라야 한다. 실패 처리는 worker 안에서
 * (파일별 상태로) 하고, 이 함수는 모든 작업이 끝날 때 resolve 한다.
 */
export async function runWithConcurrency<T>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  if (items.length === 0) return;
  const effective = Math.max(1, Math.min(limit, items.length));

  let cursor = 0;
  const runners = Array.from({ length: effective }, async () => {
    // 각 러너가 공유 커서에서 다음 인덱스를 집어간다. JS 는 단일 스레드라
    // cursor++ 사이에 다른 러너가 끼어들 수 없어 락이 필요 없다.
    for (;;) {
      const index = cursor;
      cursor += 1;
      if (index >= items.length) return;
      try {
        await worker(items[index], index);
      } catch {
        // worker 가 자기 상태에 실패를 기록한다. 여기서 삼키지 않으면
        // Promise.all 이 첫 실패에서 끊겨 남은 파일이 올라가지 않는다.
      }
    }
  });

  await Promise.all(runners);
}
